import { Component, inject, signal, ViewChildren, QueryList, ElementRef, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex items-center justify-center p-6 lg:p-8 font-sans" style="background:#f1f9fe;">
      <div class="w-full max-w-md relative">
        
        <!-- Mobile logo -->
        <div class="flex items-center justify-center mb-8">
          <img src="/logo.png" alt="NZ Plumbing" class="h-14 w-auto" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
          <div class="hidden items-center gap-3">
            <div class="w-10 h-10 rounded-2xl flex items-center justify-center" style="background:linear-gradient(135deg,#2B9AD2,#5EC9F0);">
              <svg viewBox="0 0 24 24" class="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C12 2 5 8.5 5 14a7 7 0 0014 0c0-5.5-7-12-7-12z"/>
              </svg>
            </div>
            <span class="font-bold text-slate-900 text-lg">NZ Plumbing</span>
          </div>
        </div>

        @if (resendSuccess() && step() === 'otp') {
          <div class="absolute -top-12 left-0 right-0 bg-emerald-500 text-white text-sm font-bold py-2.5 px-4 rounded-xl text-center shadow-lg animate-fade-in-down z-10">
            {{ 'newOtpSent' | t }}
          </div>
        }

        <div class="bg-white rounded-3xl p-8 shadow-sm relative" style="border:1px solid #EEF2F7;">
          @if (step() === 'credentials') {
            <!-- Login Form -->
            <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ 'welcomeBack' | t }}</h1>
            <p class="text-slate-500 text-sm mb-8">{{ 'fieldAccount' | t }}</p>

            @if (error()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
                <i class="ti ti-alert-circle text-[18px] shrink-0"></i>
                {{ error() }}
              </div>
            }

            <div class="space-y-5">
              <div>
                <label for="email" class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'emailAddress' | t }}</label>
                <input id="email" type="email" [(ngModel)]="email"
                  placeholder="plumber@nzplumbing.co.nz"
                  autocomplete="email"
                  class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all placeholder:text-slate-400"
                  style="border-color:#E2E8F0; background:#FAFBFC;"
                  onfocus="this.style.borderColor='#2B9AD2'; this.style.boxShadow='0 0 0 3px rgba(43,154,210,0.12)'"
                  onblur="this.style.borderColor='#E2E8F0'; this.style.boxShadow='none'">
              </div>

              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label for="password" class="text-sm font-semibold text-slate-700">{{ 'password' | t }}</label>
                  <a routerLink="/forgot-password" class="text-xs font-bold text-[#2B9AD2] hover:underline transition-all">{{ 'forgot' | t }}</a>
                </div>
                <div class="relative">
                  <input id="password" [type]="showPassword() ? 'text' : 'password'" [(ngModel)]="password" (keyup.enter)="verifyCredentials()"
                    autocomplete="current-password"
                  placeholder="••••••••"
                  class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all placeholder:text-slate-400"
                  style="border-color:#E2E8F0; background:#FAFBFC;"
                  onfocus="this.style.borderColor='#2B9AD2'; this.style.boxShadow='0 0 0 3px rgba(43,154,210,0.12)'"
                  onblur="this.style.borderColor='#E2E8F0'; this.style.boxShadow='none'">
                  <button
                    type="button"
                    (click)="togglePasswordVisibility()"
                    class="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors"
                    [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'">
                    <i class="ti text-[18px]" [ngClass]="showPassword() ? 'ti-eye-off' : 'ti-eye'"></i>
                  </button>
                </div>
              </div>

              <button (click)="verifyCredentials()"
                [disabled]="isLoading()"
                class="w-full h-12 text-white font-bold rounded-2xl shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center text-sm mt-2"
                style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                @if (isLoading()) {
                  <i class="ti ti-loader animate-spin mr-2"></i> {{ 'loading' | t }}
                } @else {
                  {{ 'signIn' | t }}
                }
              </button>
            </div>
          } @else {
            <!-- OTP Form -->
            <button (click)="backToLogin()" class="absolute left-6 top-6 w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
              <i class="ti ti-arrow-left text-[20px]"></i>
            </button>

            <div class="text-center mt-6">
              <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ 'verifyLogin' | t }}</h1>
              <p class="text-slate-500 text-sm mb-8">{{ 'enterCode' | t }}</p>
            </div>

            <div class="space-y-6">
              <div class="flex justify-between gap-2">
                @for (digit of otpDigits; track $index) {
                  <input 
                    #otpInput
                    type="text" inputmode="numeric"
                    maxlength="1" 
                    [(ngModel)]="otpDigits[$index]"
                    (input)="onInput($event, $index)"
                    (keydown)="onKeyDown($event, $index)"
                    (paste)="onPaste($event)"
                    class="w-10 h-12 text-xl sm:w-12 sm:h-14 sm:text-2xl text-center font-bold rounded-2xl border bg-slate-50 border-slate-200 focus:border-[#2B9AD2] focus:ring-2 focus:ring-[#2B9AD2]/10 outline-none transition-all flex-1 min-w-[40px] max-w-[50px] mx-0.5"
                    [disabled]="isLoading() || isResending()">
                }
              </div>
              
              @if (error()) {
                <div class="p-3 rounded-2xl flex items-center gap-2 text-sm font-medium" style="background:#FEF2F2; color:#EF4444;">
                  <i class="ti ti-alert-circle text-[18px]"></i>
                  {{error()}}
                </div>
              }
              
              <button 
                (click)="verifyOtp()"
                [disabled]="isLoading() || !isOtpComplete() || isResending()"
                class="flex items-center justify-center w-full h-12 text-white font-bold rounded-2xl shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                @if (isLoading()) {
                  <i class="ti ti-rotate animate-spin mr-2 text-[20px]"></i> Verifying...
                } @else {
                  {{ 'verifyContinue' | t }}
                }
              </button>
              
              <div class="text-center text-sm text-slate-500 mt-6">
                @if (resendTimer() > 0) {
                  <p>{{ 'resendCodeIn' | t }} <span class="font-bold text-slate-900">{{resendTimer()}}s</span></p>
                } @else {
                  <p>Didn't receive code? 
                    <button 
                      (click)="resendOtp()" 
                      [disabled]="isResending()"
                      class="text-[#2B9AD2] font-bold hover:underline disabled:opacity-50 disabled:no-underline ml-1">
                      @if (isResending()) {
                        {{ 'sending' | t }}
                      } @else {
                        {{ 'resend' | t }}
                      }
                    </button>
                  </p>
                }
              </div>
            </div>
          }
        </div>

        <p class="text-center text-xs text-slate-400 mt-6">
          © {{ year }} NZ Plumbing Limited. Partner access.
        </p>
      </div>
    </div>
  `,
  styles: [`
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in-down {
      animation: fadeInDown 0.3s ease-out forwards;
    }
  `]
})
export class LoginComponent implements OnDestroy {
  api = inject(ApiService);
  router = inject(Router);
  
  step = signal<'credentials' | 'otp'>('credentials');
  error = signal('');
  email = signal('');
  password = signal('');
  showPassword = signal(false);
  
  otpDigits = ['', '', '', '', '', ''];
  isLoading = signal(false);
  isResending = signal(false);
  resendSuccess = signal(false);
  resendTimer = signal(30);
  year = new Date().getFullYear();
  
  private timerInterval: ReturnType<typeof setInterval> | undefined;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  ngOnDestroy() {
    this.clearTimer();
  }

  verifyCredentials() {
    if (!this.email() || !this.password()) {
      this.error.set('Email and password are required');
      return;
    }
    
    this.isLoading.set(true);
    this.error.set('');
    
    this.api.login({ email: this.email(), password: this.password() }).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          if (!this.api.otpToken()) {
            this.error.set('The server did not start a verification session. Please deploy the updated auth API and try again.');
            return;
          }
          this.error.set('');
          this.step.set('otp');
          this.startResendTimer();
          setTimeout(() => {
            if (this.otpInputs && this.otpInputs.length > 0) {
              this.otpInputs.first.nativeElement.focus();
            }
          }, 100);
        } else {
          this.error.set(res.message || 'Login failed');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.error.set(err?.error?.message || 'Network error attempting to login');
      }
    });
  }

  togglePasswordVisibility() {
    this.showPassword.update(value => !value);
  }

  backToLogin() {
    this.step.set('credentials');
    this.clearTimer();
    this.error.set('');
    this.otpDigits = ['', '', '', '', '', ''];
  }

  startResendTimer() {
    this.resendTimer.set(30);
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.resendTimer() > 0) {
        this.resendTimer.update(v => v - 1);
      } else {
        this.clearTimer();
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  resendOtp() {
    if (!this.api.otpToken()) {
      this.error.set('Verification session expired. Please sign in again.');
      this.step.set('credentials');
      return;
    }

    this.isResending.set(true);
    this.error.set('');
    this.resendSuccess.set(false);
    
    this.api.resendOtp().subscribe({
      next: (res: any) => {
        this.isResending.set(false);
        if (res.success) {
           this.resendSuccess.set(true);
           this.otpDigits = ['', '', '', '', '', ''];
           this.startResendTimer();
           setTimeout(() => { this.resendSuccess.set(false); }, 3000);
           setTimeout(() => { if (this.otpInputs && this.otpInputs.length > 0) { this.otpInputs.first.nativeElement.focus(); } }, 100);
        } else {
           this.error.set(res.message || 'Failed to resend OTP');
        }
      },
      error: (err: any) => {
        this.isResending.set(false);
        this.error.set(err?.error?.message || 'Network error resending OTP');
      }
    });
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData) {
      const numbers = pastedData.replace(/\D/g, '').slice(0, 6);
      for (let i = 0; i < numbers.length; i++) {
        this.otpDigits[i] = numbers[i];
      }
      
      const focusIndex = Math.min(numbers.length, 5);
      setTimeout(() => {
        if (this.otpInputs && this.otpInputs.length > focusIndex) {
           this.otpInputs.toArray()[focusIndex].nativeElement.focus();
           if (this.isOtpComplete()) {
             this.verifyOtp();
           }
        }
      }, 50);
    }
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (input.value && index < 5) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    }
  }

  isOtpComplete(): boolean {
    return this.otpDigits.every(d => d.length === 1);
  }

  verifyOtp() {
    if (!this.isOtpComplete()) return;
    if (!this.api.otpToken()) {
      this.error.set('Verification session expired. Please sign in again.');
      this.step.set('credentials');
      return;
    }
    
    this.isLoading.set(true);
    this.error.set('');
    
    const code = this.otpDigits.join('');
    
    this.api.verifyOtp(code).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
           this.router.navigate(['/']);
        } else {
           this.error.set(res.message || 'Invalid verification code');
           this.otpDigits = ['', '', '', '', '', ''];
           this.otpInputs.first.nativeElement.focus();
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.error.set(err?.error?.message || 'Network error verifying code');
      }
    });
  }
}
